import {
  DEFAULT_CITY_KEY,
  displayNameForTargetCity,
  findNearestActiveCityKey,
  resolveStoredCityKey,
  searchActiveTargetCities,
  type TargetCityRow,
} from "@/lib/targetCities";

const STORAGE_KEY = "pulse.location";
const SOURCE_KEY = "pulse.location.source";
const DISPLAY_KEY = "pulse.location.display";

/** @deprecated Use DEFAULT_CITY_KEY — kept for legacy imports. */
export const DEFAULT_LOCATION = "Zurich";

export const LOCATION_DETECTING_LABEL = "Standort wird ermittelt...";

export type LocationSource = "gps" | "manual";

/**
 * @deprecated Use get_target_cities('active') via targetCities.ts — static list retained for rollback.
 */
export const CITIES = [
  "Zurich", "Geneva", "Basel", "Bern", "Lausanne", "Lucerne", "Lugano", "Winterthur",
  "Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne",
  "Vienna", "Salzburg", "Innsbruck",
  "Paris", "Lyon", "Marseille", "Nice",
  "London", "Manchester", "Edinburgh",
  "Amsterdam", "Rotterdam",
  "Barcelona", "Madrid", "Valencia",
  "Rome", "Milan", "Florence", "Naples",
  "Lisbon", "Porto",
  "Copenhagen", "Stockholm", "Oslo", "Helsinki",
  "Prague", "Budapest", "Warsaw",
  "Dublin", "Brussels",
  "New York", "Los Angeles", "Chicago", "San Francisco", "Miami", "Austin",
  "Toronto", "Montreal", "Vancouver",
  "Tokyo", "Osaka", "Kyoto",
  "Singapore", "Hong Kong", "Seoul", "Bangkok",
  "Sydney", "Melbourne",
  "Dubai", "Istanbul", "Mexico City", "São Paulo", "Buenos Aires",
];

export function readStoredCity(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function readLocationSource(): LocationSource | null {
  try {
    const raw = localStorage.getItem(SOURCE_KEY);
    if (raw === "gps" || raw === "manual") return raw;
  } catch {}
  return null;
}

export function hasStoredCity(): boolean {
  return readStoredCity() != null;
}

export function readStoredDisplayName(): string | null {
  try {
    return localStorage.getItem(DISPLAY_KEY);
  } catch {
    return null;
  }
}

/** Persist canonical city_key plus optional display label. */
export function persistLocation(cityKey: string, source: LocationSource, displayName: string) {
  try {
    localStorage.setItem(STORAGE_KEY, cityKey);
    localStorage.setItem(SOURCE_KEY, source);
    localStorage.setItem(DISPLAY_KEY, displayName);
  } catch {}
}

export function persistCity(cityKey: string, source: LocationSource, displayName?: string) {
  persistLocation(cityKey, source, displayName ?? cityKey);
}

/** Nearest active city_key from target_cities coordinates (always returns a match when list non-empty). */
export function findNearestCity(
  lat: number,
  lng: number,
  activeCities: TargetCityRow[],
): string {
  return findNearestActiveCityKey(lat, lng, activeCities);
}

type BigDataCloudReverseGeocode = {
  locality?: string;
  city?: string;
  principalSubdivision?: string;
};

/** Reverse geocode coordinates to a human place name via BigDataCloud (browser CORS-friendly). */
export async function reverseGeocodePlaceName(lat: number, lng: number): Promise<string | null> {
  try {
    const url = new URL("https://api.bigdatacloud.net/data/reverse-geocode-client");
    url.searchParams.set("latitude", String(lat));
    url.searchParams.set("longitude", String(lng));
    url.searchParams.set("localityLanguage", "de");

    const res = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;

    const data = (await res.json()) as BigDataCloudReverseGeocode;
    return data.locality ?? data.city ?? data.principalSubdivision ?? null;
  } catch {
    return null;
  }
}

export interface DetectedLocation {
  /** Active city_key for event filtering. */
  city: string;
  /** Place name for UI (suburb/town when available). */
  displayName: string;
}

/** GPS lookup with reverse geocoding for display; returns null when GPS is unavailable or denied. */
export function detectCurrentLocation(activeCities: TargetCityRow[]): Promise<DetectedLocation | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void (async () => {
          const { latitude: lat, longitude: lng } = pos.coords;
          const cityKey = findNearestCity(lat, lng, activeCities);
          const placeName = await reverseGeocodePlaceName(lat, lng);
          const matched = activeCities.find((city) => city.city_key === cityKey);
          resolve({
            city: cityKey,
            displayName: placeName ?? (matched ? displayNameForTargetCity(matched) : cityKey),
          });
        })();
      },
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  });
}

/** GPS lookup → nearest active city_key; falls back to stored key or zurich on failure. */
export function detectCurrentCity(activeCities: TargetCityRow[]): Promise<string> {
  return detectCurrentLocation(activeCities).then((result) => {
    if (result) return result.city;
    return resolveStoredCityKey(readStoredCity(), activeCities);
  });
}

export function searchCities(
  query: string,
  activeCities: TargetCityRow[],
  limit = 8,
): TargetCityRow[] {
  return searchActiveTargetCities(activeCities, query, limit);
}

export { resolveStoredCityKey, DEFAULT_CITY_KEY };
export { LocationProvider, useLocation } from "@/contexts/LocationContext";
